import {
  MEDIA_LIMITS,
  PROTOCOL_VERSION,
  fileHashDecision,
  parseChannelMessage,
  reviewFileChunk,
  reviewFileOffer,
  type FileAbort,
  type FileAccept,
  type FileAck,
  type FileChunk,
  type FileComplete,
  type FileMessage,
  type FileOffer,
  type ReviewFailure,
} from "wylo-rc-session";
import { bytesToBase64, base64ToBytes } from "./messages.ts";

export type OutboundFile =
  | {
      ok: true;
      offer: FileOffer;
      chunks: FileChunk[];
      complete: FileComplete;
    }
  | { ok: false; reason: ReviewFailure | "invalid" };

export type InboundState =
  | { kind: "idle" }
  | { kind: "receiving"; offer: FileOffer; parts: Uint8Array[] }
  | { kind: "done"; name: string; bytes: Uint8Array; sha256: string }
  | { kind: "aborted"; reason: string };

export type InboundStep = {
  state: InboundState;
  reply?: FileMessage;
  progress?: { name: string; bytes: number; status: "transferring" | "complete" | "aborted"; reason?: string };
};

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const copy = new Uint8Array(bytes);
  const digest = await crypto.subtle.digest("SHA-256", copy);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function checked(channelMessage: FileMessage): FileMessage | undefined {
  const parsed = parseChannelMessage("files", channelMessage);
  if (!parsed.ok) return undefined;
  return parsed.value;
}

export async function planOutboundFile(input: {
  bytes: Uint8Array;
  name: string;
  enabled: boolean;
  activeTransfers: number;
  transferId: string;
  now: string;
}): Promise<OutboundFile> {
  if (input.name.length === 0 || input.name.length > 255) return { ok: false, reason: "invalid" };
  const sha256 = await sha256Hex(input.bytes);
  const offer: FileOffer = {
    v: PROTOCOL_VERSION,
    type: "file.offer",
    id: crypto.randomUUID(),
    ts: input.now,
    transferId: input.transferId,
    direction: "ToDevice",
    name: input.name,
    sha256,
    bytes: input.bytes.byteLength,
    chunkBytes: MEDIA_LIMITS.fileChunkBytes,
  };
  const review = reviewFileOffer({ offer, fileTransferEnabled: input.enabled, activeTransfers: input.activeTransfers });
  if (!review.ok) return review;
  const parsedOffer = checked(offer);
  if (!parsedOffer || parsedOffer.type !== "file.offer") return { ok: false, reason: "invalid" };
  const chunks: FileChunk[] = [];
  const size = MEDIA_LIMITS.fileChunkBytes;
  let index = 0;
  for (let offset = 0; offset < input.bytes.byteLength; offset += size) {
    const slice = input.bytes.subarray(offset, offset + size);
    const chunk: FileChunk = {
      v: PROTOCOL_VERSION,
      type: "file.chunk",
      id: crypto.randomUUID(),
      ts: input.now,
      transferId: input.transferId,
      index,
      payloadBase64: bytesToBase64(slice),
    };
    const chunkReview = reviewFileChunk({ chunk });
    if (!chunkReview.ok) return chunkReview;
    const parsedChunk = checked(chunk);
    if (!parsedChunk || parsedChunk.type !== "file.chunk") return { ok: false, reason: "invalid" };
    chunks.push(parsedChunk);
    index += 1;
  }
  const complete: FileComplete = {
    v: PROTOCOL_VERSION,
    type: "file.complete",
    id: crypto.randomUUID(),
    ts: input.now,
    transferId: input.transferId,
    sha256,
  };
  const parsedComplete = checked(complete);
  if (!parsedComplete || parsedComplete.type !== "file.complete") return { ok: false, reason: "invalid" };
  return { ok: true, offer: parsedOffer, chunks, complete: parsedComplete };
}

export async function applyInboundFile(state: InboundState, message: FileMessage, enabled: boolean, activeTransfers: number, now: string): Promise<InboundStep> {
  if (message.type === "file.offer") {
    if (state.kind === "receiving") {
      return abort(message.transferId, "limit", now, state);
    }
    const review = reviewFileOffer({ offer: message, fileTransferEnabled: enabled, activeTransfers });
    if (!review.ok) return abort(message.transferId, review.reason === "disabled" ? "rejected" : "limit", now, { kind: "idle" });
    if (message.direction !== "ToOperator") return { state };
    const accept: FileAccept = {
      v: PROTOCOL_VERSION,
      type: "file.accept",
      id: crypto.randomUUID(),
      ts: now,
      transferId: message.transferId,
    };
    const reply = checked(accept);
    if (!reply) return abort(message.transferId, "error", now, { kind: "idle" });
    return {
      state: { kind: "receiving", offer: message, parts: [] },
      reply,
      progress: { name: message.name, bytes: message.bytes, status: "transferring" },
    };
  }
  if (state.kind !== "receiving" || message.transferId !== state.offer.transferId) return { state };
  if (message.type === "file.chunk") {
    const review = reviewFileChunk({ chunk: message });
    if (!review.ok) return abort(message.transferId, "limit", now, state);
    const bytes = base64ToBytes(message.payloadBase64);
    if (!bytes) return abort(message.transferId, "error", now, state);
    const parts = state.parts.slice();
    parts[message.index] = bytes;
    return { state: { ...state, parts }, progress: { name: state.offer.name, bytes: state.offer.bytes, status: "transferring" } };
  }
  if (message.type === "file.abort") {
    return {
      state: { kind: "aborted", reason: message.reason },
      progress: { name: state.offer.name, bytes: state.offer.bytes, status: "aborted", reason: message.reason },
    };
  }
  if (message.type === "file.complete") {
    const merged = concat(state.parts);
    const actual = await sha256Hex(merged);
    const decision = fileHashDecision(message.sha256, actual);
    if (decision === "hash-mismatch" || actual !== state.offer.sha256) {
      return abort(message.transferId, "hash-mismatch", now, state);
    }
    const ack: FileAck = {
      v: PROTOCOL_VERSION,
      type: "file.ack",
      id: crypto.randomUUID(),
      ts: now,
      transferId: message.transferId,
      sha256: actual,
    };
    const reply = checked(ack);
    if (!reply) return abort(message.transferId, "error", now, state);
    return {
      state: { kind: "done", name: state.offer.name, bytes: merged, sha256: actual },
      reply,
      progress: { name: state.offer.name, bytes: merged.byteLength, status: "complete" },
    };
  }
  return { state };
}

function concat(parts: Uint8Array[]): Uint8Array {
  const length = parts.reduce((sum, part) => sum + (part?.byteLength ?? 0), 0);
  const merged = new Uint8Array(length);
  let offset = 0;
  for (const part of parts) {
    if (!part) continue;
    merged.set(part, offset);
    offset += part.byteLength;
  }
  return merged;
}

function abort(transferId: string, reason: FileAbort["reason"], now: string, state: InboundState): InboundStep {
  const name = state.kind === "receiving" ? state.offer.name : "";
  const bytes = state.kind === "receiving" ? state.offer.bytes : 0;
  const message: FileAbort = {
    v: PROTOCOL_VERSION,
    type: "file.abort",
    id: crypto.randomUUID(),
    ts: now,
    transferId,
    reason,
  };
  return {
    state: { kind: "aborted", reason },
    reply: checked(message),
    progress: { name, bytes, status: "aborted", reason },
  };
}
